import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Box,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  IconButton,
  Breadcrumbs,
  Skeleton,
  Alert,
  Snackbar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Rating,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowBack,
  LocationOn,
  Language,
  Email,
  Phone,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  Verified,
  People,
  Event,
  EmojiEvents,
  Share,
  Favorite,
  FavoriteBorder,
  Message,

  CalendarToday,
  Euro,
  Visibility
} from '@mui/icons-material';
import { 
  selectSelectedOrganization,
  selectLoading,
  selectError,
  fetchOrganizationById,
  clearErrors
} from '../redux/slices/organisationsSlice';
import './OrganisationProfile.css';

const OrganisationProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const organisation = useSelector(selectSelectedOrganization);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: ''
  });

  useEffect(() => {
    if (id && !organisation && !loading) {
      dispatch(fetchOrganizationById(id));
    }
  }, [id, organisation, loading, dispatch]);

  useEffect(() => {
    // Simulate checking if organization is in favorites
    const favorites = JSON.parse(localStorage.getItem('favoriteOrganisations') || '[]');
    setIsFavorite(favorites.includes(id));
  }, [id]);

  const handleBack = () => {
    navigate('/organisations');
  };

  const handleContact = () => {
    setShowContactDialog(true);
  };

  const handleSendMessage = () => {
    // Simulate sending message
    console.log('Sending message:', contactForm);
    setShowContactDialog(false);
    setContactForm({ subject: '', message: '' });
    setSnackbar({
      open: true,
      message: 'Message envoyé avec succès !',
      severity: 'success'
    });
  };

  const handleToggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('favoriteOrganisations') || '[]');
    let newFavorites;
    
    if (isFavorite) {
      newFavorites = favorites.filter(fav => fav !== id);
      setSnackbar({
        open: true,
        message: 'Retiré des favoris',
        severity: 'info'
      });
    } else {
      newFavorites = [...favorites, id];
      setSnackbar({
        open: true,
        message: 'Ajouté aux favoris',
        severity: 'success'
      });
    }
    
    localStorage.setItem('favoriteOrganisations', JSON.stringify(newFavorites));
    setIsFavorite(!isFavorite);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: organisation?.name,
          text: organisation?.description,
          url: window.location.href
        });
      } catch (error) {
        // User canceled the share or an error occurred
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          setShowShareDialog(true);
        }
        // If it's an AbortError (user canceled), we silently ignore it
      }
    } else {
      setShowShareDialog(true);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    setShowShareDialog(false);
    setSnackbar({
      open: true,
      message: 'Lien copié dans le presse-papiers',
      severity: 'success'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseError = () => {
    dispatch(clearErrors());
  };

  const formatMemberCount = (count) => {
    if (!count && count !== 0) {
      return '0';
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook': return <Facebook />;
      case 'twitter': return <Twitter />;
      case 'instagram': return <Instagram />;
      case 'linkedin': return <LinkedIn />;
      default: return <Language />;
    }
  };

  if (loading) {
    return (
      <div className="organisation-profile-page">
        <Container maxWidth="lg">
          {/* Header Skeleton */}
          <Box className="profile-header">
            <Skeleton variant="rectangular" height={300} className="cover-image" />
            <Box className="profile-info">
              <Skeleton variant="circular" width={120} height={120} className="profile-avatar" />
              <Box className="profile-details">
                <Skeleton variant="text" width={300} height={40} />
                <Skeleton variant="text" width={200} height={24} />
                <Skeleton variant="text" width={150} height={20} />
              </Box>
            </Box>
          </Box>
          
          {/* Content Skeleton */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
          </Grid>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert 
          severity="error" 
          onClose={handleCloseError}
          sx={{ mt: 2 }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!organisation) {
    return (
      <Container maxWidth="lg">
        <Alert severity="warning" sx={{ mt: 2 }}>
          Organisation non trouvée
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{organisation.name} - Mission Sport</title>
        <meta name="description" content={organisation.description} />
        <meta property="og:title" content={`${organisation.name} - Mission Sport`} />
        <meta property="og:description" content={organisation.description} />
        <meta property="og:image" content={organisation.coverImage} />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="organisation-profile-page">
        {/* Navigation */}
        <Container maxWidth="lg">
          <Box className="navigation-bar">
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              className="back-button"
            >
              Retour aux organisations
            </Button>
            
            <Breadcrumbs>
              <Link to="/" className="breadcrumb-link">Accueil</Link>
              <Link to="/organisations" className="breadcrumb-link">Organisations</Link>
              <Typography color="text.primary">{organisation.name}</Typography>
            </Breadcrumbs>
          </Box>
        </Container>

        {/* Profile Header */}
        <Box className="profile-header">
          <Box 
            className="cover-image"
            style={{ backgroundImage: `url(${organisation.coverImage})` }}
          >
            <Box className="cover-overlay" />
          </Box>
          
          <Container maxWidth="lg">
            <Box className="profile-info">
              <Avatar
                src={organisation.logo}
                alt={organisation.name}
                className="profile-avatar"
              >
                {organisation.name.charAt(0)}
              </Avatar>
              
              <Box className="profile-details">
                <Box className="profile-title">
                  <Typography variant="h3" component="h1" className="organisation-name">
                    {organisation.name}
                    {organisation.verified && (
                      <Tooltip title="Organisation vérifiée">
                        <Verified className="verified-badge" />
                      </Tooltip>
                    )}
                  </Typography>
                  
                  <Box className="profile-actions">
                    <Tooltip title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}>
                      <IconButton onClick={handleToggleFavorite} className="action-button">
                        {isFavorite ? <Favorite /> : <FavoriteBorder />}
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Partager">
                      <IconButton onClick={handleShare} className="action-button">
                        <Share />
                      </IconButton>
                    </Tooltip>
                    
                    <Button
                      variant="contained"
                      startIcon={<Message />}
                      onClick={handleContact}
                      className="contact-button"
                    >
                      Contacter
                    </Button>
                  </Box>
                </Box>
                
                <Box className="profile-meta">
                  <Chip 
                    label={organisation.type}
                    className={`type-chip type-${organisation.type.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                  
                  <Box className="location-info">
                    <LocationOn className="location-icon" />
                    <Typography variant="body2">
                      {organisation.location.city}, {organisation.location.country}
                    </Typography>
                  </Box>
                  
                  <Box className="stats">
                    <Box className="stat-item">
                      <People className="stat-icon" />
                      <Typography variant="body2">
                        {formatMemberCount(organisation.memberCount)} membres
                      </Typography>
                    </Box>
                    
                    <Box className="stat-item">
                      <Event className="stat-icon" />
                      <Typography variant="body2">
                        {organisation.missions?.length || 0} missions
                      </Typography>
                    </Box>
                    
                    {organisation.rating && (
                      <Box className="stat-item">
                        <Rating value={organisation.rating} readOnly size="small" />
                        <Typography variant="body2">
                          ({organisation.reviewCount} avis)
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Main Content */}
        <Container maxWidth="lg">
          <Grid container spacing={4} className="main-content">
            {/* Left Column - Main Info */}
            <Grid item xs={12} md={8}>
              {/* Description */}
              <Paper className="content-section">
                <Typography variant="h5" component="h2" className="section-title">
                  À propos
                </Typography>
                <Typography variant="body1" className="description">
                  {organisation.description}
                </Typography>
              </Paper>

              {/* Sports Disciplines */}
              <Paper className="content-section">
                <Typography variant="h5" component="h2" className="section-title">
                  Disciplines sportives
                </Typography>
                <Box className="disciplines-grid">
                  {organisation.sports && organisation.sports.map((sport, index) => (
                    <Chip
                      key={index}
                      label={sport}
                      className="sport-chip"
                      icon={<EmojiEvents />}
                    />
                  ))}
                </Box>
              </Paper>

              {/* Missions */}
              <Paper className="content-section">
                <Typography variant="h5" component="h2" className="section-title">
                  Missions publiées ({organisation.missions?.length || 0})
                </Typography>
                
                {organisation.missions && organisation.missions.length > 0 ? (
                  <Grid container spacing={2}>
                    {organisation.missions.map((mission) => (
                      <Grid item xs={12} sm={6} key={mission.id}>
                        <Card className="mission-card">
                          <CardMedia
                            component="img"
                            height="120"
                            image={mission.image}
                            alt={mission.title}
                          />
                          <CardContent>
                            <Typography variant="h6" className="mission-title">
                              {mission.title}
                            </Typography>
                            
                            <Box className="mission-meta">
                              <Box className="mission-date">
                                <CalendarToday className="meta-icon" />
                                <Typography variant="body2">
                                  {new Date(mission.date).toLocaleDateString('fr-FR')}
                                </Typography>
                              </Box>
                              
                              <Box className="mission-price">
                                <Euro className="meta-icon" />
                                <Typography variant="body2">
                                  {mission.price}€
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Button
                              component={Link}
                              to={`/missions/${mission.id}`}
                              variant="outlined"
                              size="small"
                              startIcon={<Visibility />}
                              className="view-mission-button"
                            >
                              Voir détails
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box className="no-missions">
                    <Event className="no-missions-icon" />
                    <Typography variant="body1" color="text.secondary">
                      Aucune mission publiée pour le moment
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Right Column - Contact & Info */}
            <Grid item xs={12} md={4}>
              {/* Contact Information */}
              <Paper className="sidebar-section">
                <Typography variant="h6" className="sidebar-title">
                  Informations de contact
                </Typography>
                
                <List className="contact-list">
                  {organisation.contact?.email && (
                    <ListItem className="contact-item">
                      <ListItemIcon>
                        <Email className="contact-icon" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Email"
                        secondary={
                          <a href={`mailto:${organisation.contact?.email}`} className="contact-link">
                            {organisation.contact?.email}
                          </a>
                        }
                      />
                    </ListItem>
                  )}
                  
                  {organisation.contact?.phone && (
                    <ListItem className="contact-item">
                      <ListItemIcon>
                        <Phone className="contact-icon" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Téléphone"
                        secondary={
                          <a href={`tel:${organisation.contact?.phone}`} className="contact-link">
                            {organisation.contact?.phone}
                          </a>
                        }
                      />
                    </ListItem>
                  )}
                  
                  {organisation.website && (
                    <ListItem className="contact-item">
                      <ListItemIcon>
                        <Language className="contact-icon" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Site web"
                        secondary={
                          <a 
                            href={organisation.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="contact-link"
                          >
                            Visiter le site
                          </a>
                        }
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>

              {/* Social Media */}
              {organisation.socialMedia && Object.keys(organisation.socialMedia).length > 0 && (
                <Paper className="sidebar-section">
                  <Typography variant="h6" className="sidebar-title">
                    Réseaux sociaux
                  </Typography>
                  
                  <Box className="social-links">
                    {organisation.socialMedia && Object.entries(organisation.socialMedia).map(([platform, url]) => (
                      <Tooltip key={platform} title={platform}>
                        <IconButton
                          component="a"
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-button"
                        >
                          {getSocialIcon(platform)}
                        </IconButton>
                      </Tooltip>
                    ))}
                  </Box>
                </Paper>
              )}

              {/* Quick Stats */}
              <Paper className="sidebar-section">
                <Typography variant="h6" className="sidebar-title">
                  Statistiques
                </Typography>
                
                <Box className="quick-stats">
                  <Box className="quick-stat">
                    <Typography variant="h4" className="stat-number">
                      {formatMemberCount(organisation.memberCount)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Membres
                    </Typography>
                  </Box>
                  
                  <Divider orientation="vertical" flexItem />
                  
                  <Box className="quick-stat">
                    <Typography variant="h4" className="stat-number">
                      {organisation.missions?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Missions
                    </Typography>
                  </Box>
                  
                  <Divider orientation="vertical" flexItem />
                  
                  <Box className="quick-stat">
                    <Typography variant="h4" className="stat-number">
                      {organisation.sports?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sports
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </div>

      {/* Contact Dialog */}
      <Dialog
        open={showContactDialog}
        onClose={() => setShowContactDialog(false)}
        maxWidth="sm"
        fullWidth
        className="contact-dialog"
      >
        <DialogTitle>
          Contacter {organisation.name}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Sujet"
            fullWidth
            variant="outlined"
            value={contactForm.subject}
            onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={contactForm.message}
            onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowContactDialog(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleSendMessage}
            variant="contained"
            disabled={!contactForm.subject || !contactForm.message}
          >
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <Dialog
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Partager cette organisation</DialogTitle>
        <DialogContent>
          <Button
            fullWidth
            variant="outlined"
            onClick={copyToClipboard}
            sx={{ mb: 1 }}
          >
            Copier le lien
          </Button>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OrganisationProfile;